import clsx from 'clsx';
import { TFunction } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EnumToArrayValues, stringToCapitalizedWord } from '@vestico/utils';

import { HeightUnit, SizeChartDisplayResult, getDefaultHeightUnit } from '@vestico/model';

import { SegmentedControl } from '../../components/SegmentedControl';
import style from './SizeRecommendationTabSizeChart.module.css';
import { SizeRecommendationTabSizeChartItems } from './SizeRecommendationTabSizeChartItems';

const stringForHeightUnit = (v: HeightUnit, t: TFunction) => {
  switch (v) {
    case HeightUnit.centimeter:
      return t('LOC_BASIC_INFO_UNIT_HEIGHT_CM');
    case HeightUnit.feet:
      return t('LOC_BASIC_INFO_UNIT_HEIGHT_INCHES');
  }
  throw new Error(`Missing height unit ${v}`);
};

export interface SizeRecommendationTabSizeChartProps {
  sizeChart: SizeChartDisplayResult;
  title?: string;
}

export const SizeRecommendationTabSizeChart = ({ sizeChart, title }: SizeRecommendationTabSizeChartProps) => {
  const { t, i18n } = useTranslation('shared');
  const { items, additionalOptions, flipped, description } = sizeChart;

  const units: HeightUnit[] = EnumToArrayValues(HeightUnit);
  const options = useMemo(() => units.map((v) => stringForHeightUnit(v, t)), [t, units]);
  const [selectedHeightUnit, setSelectedHeightUnit] = useState(getDefaultHeightUnit(i18n.language));
  const onUpdateHeightUnit = useCallback((index: number) => setSelectedHeightUnit(index), []);

  return (
    <div className={clsx(style.root)}>
      {title ? <h3 className={clsx(style.title)}>{title}</h3> : null}
      <div className={clsx(style.units)}>
        <SegmentedControl
          configId="unit"
          options={options}
          selectedOptionIndex={selectedHeightUnit}
          didUpdateOption={onUpdateHeightUnit}
          height="slim"
        />
      </div>
      <div className={clsx(style.chartsOuter)}>
        <div className={clsx(style.chartsInner)}>
          <SizeRecommendationTabSizeChartItems items={items} unit={selectedHeightUnit} flipped={flipped} description={description} />
          {additionalOptions.map((option, index) => (
            <SizeRecommendationTabSizeChartItems
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              title={stringToCapitalizedWord(option.productAttribute)}
              items={option.items}
              unit={selectedHeightUnit}
              flipped={flipped}
              description={option.description}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
